import { Singleton } from 'typescript-ioc/es5'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'

/**
 * Client for the sailfish AI generation directive CRUD (NS-2649):
 *   GET   /domains/:domainId/ai-generation-directives[?role=]
 *   POST  /domains/:domainId/ai-generation-directives
 *   PATCH /domains/:domainId/ai-generation-directives/:directiveId
 *
 * The sailfish controller runs with its payload case-converter disabled and
 * validates the snake-case `@pushly/models` structs, so this service uses those
 * structs directly with `skipCaseConversion` (no camelCase bridging).
 */
@Singleton
export class AiGenerationDirectiveService extends AbstractRequestService {
    public constructor() {
        super()
    }

    /** Resolve the domain's Brand Voice directive (the singleton `brand_voice`), or null. */
    public async fetchBrandVoice(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective | null>> {
        const res = await this.request(HttpMethod.GET, `${this.getEndpoint(domainId)}?role=brand_voice`, {
            ...options,
            config: { skipCaseConversion: true },
            responseType: [AiGenerationDirective],
        })

        return { ...res, data: res.data?.[0] ?? null }
    }

    public async create(
        domainId: number,
        dto: CreateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.POST, this.getEndpoint(domainId), {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    public async update(
        domainId: number,
        directiveId: number,
        dto: UpdateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.PATCH, `${this.getEndpoint(domainId)}/${directiveId}`, {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    private getEndpoint(domainId: number): string {
        return `${aqe.defaults.publicApiDomain}/domains/${domainId}/ai-generation-directives`
    }
}
